import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  HeaderContainer,
  Header
} from 'carbon-components-react/lib/components/UIShell';
import { Notifications, TopHeader } from 'components';
import { LeftHandSideNav, LeftHandSideRail } from '..';
import { MapStyleControllerPanel } from 'components/MapStyleController';
import { MapLocationSearchPanel } from 'components/MapLocationSearch';
import { setHeaderPanelExpand } from 'redux/app/actions';
import { selectHeaderPanelExpand } from 'redux/app/selectors';
import { WarningFilled16 } from '@carbon/icons-react';
import {
  selectorAlertNotificationShowHide,
  selectorAlertNotificationMessage
} from 'redux/session/selectors';
import { fetchAlertNotificationStatusStart } from 'redux/session/actions';

export interface HeaderProps {
  disableNav?: boolean;
  enableHeaderGlobalBar: boolean;
  stickyTopHeaderObj?: object;
  sideRailButtons?: object;
  sideRailExpandableMenu?: object;
  showLeftHandSideNav?: boolean;
  mapStyleClusterHandler?: Function;
  mapStyleClusterVisibleRef?: React.MutableRefObject<boolean>;
  mapStyleShowMasking?: boolean;
  mapStyleShowLegend?: React.ReactElement;
}
/**
 * MAIN HEADER CONTAINER
 */
export const MainHeaderContainer: React.FC<HeaderProps & any> = React.memo(
  (props: any) => {
    const {
      disableNav,
      enableHeaderGlobalBar,
      sideRailButtons,
      showLeftHandSideNav = true,
      mapStyleShowLegend,
      mapStyleShowMasking,
      mapStyleClusterHandler,
      mapStyleClusterVisibleRef
    } = props;
    const [isSideNavExpanded, setIsSideNavExpanded] = useState<boolean>(false);

    const dispatch = useDispatch();
    const headerPanelExpand: any = useSelector(selectHeaderPanelExpand);
    const alertNotificationToggle = useSelector(
      selectorAlertNotificationShowHide
    );
    const alertNotificationMessage = useSelector(
      selectorAlertNotificationMessage
    );

    const hamburgerMenuHandler = useCallback(() => {
      if (!isSideNavExpanded) {
        dispatch(setHeaderPanelExpand({ isOpen: false }));
      }
      setIsSideNavExpanded(!isSideNavExpanded);
    }, [isSideNavExpanded, dispatch]);

    useEffect(() => {
      if (headerPanelExpand?.isOpen) {
        setIsSideNavExpanded(false);
      }
    }, [headerPanelExpand]);

    useEffect(() => {
      dispatch(fetchAlertNotificationStatusStart());
    }, [dispatch]);

    return (
      <HeaderContainer
        render={() => (
          <>
            {alertNotificationToggle && (
              <div className="noti-alert-notification-container">
                <WarningFilled16 className="noti-alert-notification-svg" />
                <span
                  className="noti-alert-notification-text"
                  dangerouslySetInnerHTML={{ __html: alertNotificationMessage }}
                />
              </div>
            )}
            <Header
              aria-label="global header"
              className={`${
                alertNotificationToggle ? 'noti-alert-header-container' : ''
              } global-header`}
            >
              {props.children}
              <TopHeader
                disableNav={disableNav}
                enableHeaderGlobalBar={
                  enableHeaderGlobalBar ? enableHeaderGlobalBar : false
                }
                hamburgerMenuHandler={hamburgerMenuHandler}
                isSideNavExpanded={isSideNavExpanded}
                showLeftHandSideNav={showLeftHandSideNav}
              >
                {sideRailButtons && (
                  <div className="map-action--buttons  bx--col-sm-0  bx--col-md-0">
                    <LeftHandSideRail sideRailButtons={sideRailButtons} />
                  </div>
                )}
              </TopHeader>
              {showLeftHandSideNav && (
                <LeftHandSideNav
                  disableNav={disableNav}
                  isSideNavExpanded={isSideNavExpanded}
                  setIsSideNavExpanded={setIsSideNavExpanded}
                />
              )}
              <MapStyleControllerPanel
                clusterHandler={mapStyleClusterHandler}
                clusterVisibleRef={mapStyleClusterVisibleRef}
                showMasking={mapStyleShowMasking}
                showLegend={mapStyleShowLegend}
              />
              <MapLocationSearchPanel />
              <Notifications />
            </Header>
          </>
        )}
      />
    );
  }
);
MainHeaderContainer.displayName = 'MainHeaderContainer';
